import { useAtom } from "jotai";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useState } from "react";

import type { RefineSearchState } from "scmp-app/components/search/contexts";
import { EmptyRefineSearchState, useSearchContext } from "scmp-app/components/search/contexts";
import { RefineSearchPanel } from "scmp-app/components/search/search-result-panel/refine-search";

import { refineSearchDrawerAtom } from "./atoms";
import {
  ApplyButton,
  Body,
  CloseButton,
  Container,
  Header,
  ResetButton,
  StyledCloseIcon,
  StyledDynamicDrawer,
  Title,
} from "./styles";

type Props = {
  className?: string;
};

export const RefineSearchDrawer: FunctionComponent<Props> = ({ className }) => {
  const [{ isOpen }, setDrawer] = useAtom(refineSearchDrawerAtom);

  const { refineSearch, setRefineSearch } = useSearchContext();
  const [pending, setPending] = useState(refineSearch);

  useEffect(() => {
    if (isOpen) setPending(refineSearch);
  }, [isOpen, refineSearch]);

  const close = useCallback(() => setDrawer({ isOpen: false }), [setDrawer]);

  const handleOnchange = useCallback((state: Partial<RefineSearchState>) => {
    setPending(current => ({ ...current, ...state }));
  }, []);

  const handleApply = useCallback(() => {
    setRefineSearch(pending);
    close();
  }, [close, pending, setRefineSearch]);

  return (
    <StyledDynamicDrawer anchor="right" onClose={close} open={isOpen} withDrawerCardHeader={false}>
      <Container className={className}>
        <Header>
          <CloseButton onClick={close} type="button">
            <StyledCloseIcon />
          </CloseButton>
          <Title>Refine Search</Title>
          <ResetButton
            onClick={() => {
              setRefineSearch(EmptyRefineSearchState);
              setPending(EmptyRefineSearchState);
              close();
            }}
            type="button"
          >
            Reset
          </ResetButton>
        </Header>

        <Body>
          <RefineSearchPanel onChange={handleOnchange} value={pending} />
        </Body>

        <ApplyButton onClick={handleApply} type="button">
          REFINE SEARCH
        </ApplyButton>
      </Container>
    </StyledDynamicDrawer>
  );
};

RefineSearchDrawer.displayName = "RefineSearchDrawer";
